import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import {
  getPropertyDocuments,
  uploadPropertyDocument,
  replacePropertyDocument,
  deletePropertyDocument,
  getPropertyDocumentDownload,
} from "../../api/documentApi";
import { formatDateUk } from "../../utils/dateDisplay";

const TARGET_LABELS = {
  insurance: "Insurance Policy",
  utility: "Utility",
  assessment: "Assessment",
  unit: "Unit",
};

function DocumentManager({ propertyId, targetType, targetId, title = "Documents" }) {
  const { user, permissions } = useAuth();
  const userRoles = user?.claims_role || [];
  const canEdit = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("PUT")
  );
  const canCreate = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("POST")
  );
  const canDelete = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("DELETE")
  );

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [replacingId, setReplacingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const ready = Boolean(propertyId && targetId);

  const loadDocuments = useCallback(async () => {
    if (!propertyId || !targetId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await getPropertyDocuments(propertyId);
      const filtered = (data || []).filter(
        (doc) =>
          doc.source_type === targetType &&
          String(doc.source_id) === String(targetId)
      );
      setDocuments(filtered);
    } catch (err) {
      console.error(err);
      setError("Failed to load documents.");
    } finally {
      setLoading(false);
    }
  }, [propertyId, targetId, targetType]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleUpload = async (file) => {
    if (!file || !ready) return;
    setUploading(true);
    try {
      const payload = {
        insurancePolicyId: targetType === "insurance" ? targetId : undefined,
        utilityId: targetType === "utility" ? targetId : undefined,
        assessmentId: targetType === "assessment" ? targetId : undefined,
        unitId: targetType === "unit" ? targetId : undefined,
      };
      await uploadPropertyDocument(propertyId, file, payload);
      await loadDocuments();
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to upload document.";
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  const handleReplace = async (documentId, file) => {
    if (!file || !propertyId) return;
    setReplacingId(documentId);
    try {
      await replacePropertyDocument(propertyId, documentId, file);
      await loadDocuments();
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to replace document.";
      setError(message);
    } finally {
      setReplacingId(null);
    }
  };

  const handleDelete = async (documentId) => {
    if (!propertyId) return;
    const confirmed = window.confirm("Delete this document?");
    if (!confirmed) return;
    setDeletingId(documentId);
    try {
      await deletePropertyDocument(propertyId, documentId);
      await loadDocuments();
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to delete document.";
      setError(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (doc) => {
    if (!propertyId) return;
    try {
      const response = await getPropertyDocumentDownload(propertyId, doc.id);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const filename = match ? decodeURIComponent(match[1]) : doc.file_name || "document";
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || "Failed to download document.";
      setError(message);
    }
  };

  const formatDate = formatDateUk;

  const uploadInputId = `document-upload-${targetType}-${targetId || "new"}`;

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            onClick={() => document.getElementById(uploadInputId)?.click()}
            disabled={uploading || !ready}
          >
            {uploading ? "Uploading..." : "Upload Document"}
          </Button>
        )}
      </Box>

      {!ready && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Save this {TARGET_LABELS[targetType] || "record"} to add documents.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <input
        id={uploadInputId}
        type="file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            handleUpload(file);
          }
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading documents...
        </Typography>
      ) : documents.length ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File</TableCell>
                <TableCell>Uploaded</TableCell>
                {(canEdit || canDelete) && (
                  <TableCell align="right">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>{doc.file_name || `Document ${doc.id}`}</TableCell>
                  <TableCell>{formatDate(doc.uploaded_at_utc)}</TableCell>
                  {(canEdit || canDelete) && (
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDownload(doc)}
                        >
                          Download
                        </Button>
                        {canEdit && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                document
                                  .getElementById(`document-replace-${doc.id}`)
                                  ?.click()
                              }
                              disabled={replacingId === doc.id}
                            >
                              {replacingId === doc.id ? "Replacing..." : "Replace"}
                            </Button>
                            <input
                              id={`document-replace-${doc.id}`}
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleReplace(doc.id, file);
                                }
                                e.target.value = "";
                              }}
                              style={{ display: "none" }}
                            />
                          </>
                        )}
                        {canDelete && (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleDelete(doc.id)}
                            disabled={deletingId === doc.id}
                          >
                            {deletingId === doc.id ? "Deleting..." : "Delete"}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No documents yet.
        </Typography>
      )}
    </Paper>
  );
}

export default DocumentManager;
